import { createSelector } from '@reduxjs/toolkit'
import type { NodeV2, NodeV2Style } from '@vedalib/editor'
import { isComplexElement, isNodeV2, isShellNode, isSlotElement } from '@vedalib/editor'
import type { ElementsTypes } from '@vedalib/editor/lib/elements'
import type { DeepPartial } from '@vedalib/editor/lib/utils/types'
import lodash from 'lodash'
import * as R from 'ramda'

import { getParentNode } from 'components/Editor/EditorContext/selectors/block'
import {
  DEFAULT_NODE_STYLE_VALUES,
  FIT_ELEMENTS,
  FIT_STYLES,
} from 'components/Editor/NodeV2/constants'
import { DEVICE_DESK_ORDER } from 'components/Editor/types/data.constants'
import { EMPTY_ARRAY } from 'constants/commonConstans'
import { notEmpty } from 'utils/notEmpty'

import { DeviceMode } from '../../enums'
import type { Block, IProjectContext, MapType, NodeState } from '../../types'
import { getBlock } from '../data/block'
import { getDeviceMode } from './editor'

const mergeCustomize = <T>(objValue: T, srcValue: T) => {
  if (lodash.isArray(objValue) || lodash.isArray(srcValue)) {
    return srcValue
  }

  return
}

export const mergeNodeStyle = (
  obj: { [key in DeviceMode]?: DeepPartial<NodeV2Style> } = {},
  deviceMode: DeviceMode = DeviceMode.desktop,
  elementType?: ElementsTypes,
) => {
  const index = DEVICE_DESK_ORDER.indexOf(deviceMode)
  const matchDevices = DEVICE_DESK_ORDER.slice(0, index + 1)
  return matchDevices.reduce<NodeV2Style>(
    (acc, deviceMode) => lodash.mergeWith(acc, obj[deviceMode], mergeCustomize),
    {
      ...DEFAULT_NODE_STYLE_VALUES,
      ...(elementType && FIT_ELEMENTS.includes(elementType) && FIT_STYLES),
    },
  )
}

export const getNode = (
  state: IProjectContext,
  blockId: string | null,
  nodeId: string | null | undefined,
): NodeV2 | null => {
  const block = blockId && state.data.blocks[blockId]
  const node = nodeId && block && block?.schema.nodes[nodeId]
  if (!isNodeV2(node)) {
    return null
  }
  return node
}

export const fullMergeNodeStyle = (
  block?: Block | null,
  node?: NodeV2 | null,
  device: DeviceMode = DeviceMode.desktop,
) => {
  if (node?.elementId) {
    const element = block?.elements[node.elementId]
    return node?.style ? mergeNodeStyle(node.style, device, element?.type) : null
  }

  return node?.style ? mergeNodeStyle(node.style, device) : null
}

export const getNodeStyle = createSelector(
  [(context, id) => getBlock(context, { id }), getNode, getDeviceMode],
  fullMergeNodeStyle,
)

export const getNodeStyleByDevice = createSelector(
  [
    (context, id) => getBlock(context, { id }),
    getNode,
    (_context, _blockId: string, _nodeId: string, device: DeviceMode) => device,
  ],
  (block, node, device) => {
    if (node?.elementId) {
      const element = block?.elements[node.elementId]
      return mergeNodeStyle(node?.style || {}, device, element?.type)
    }

    return mergeNodeStyle(node?.style || {}, device)
  },
)

export const getNodeState = createSelector(
  (state: IProjectContext) => state.state.nodes,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (nodes, nodeId) => nodes[nodeId] as NodeState | undefined,
)
export const getNodeStatePlaying = createSelector(
  (state: IProjectContext, nodeId: string) => state.state.nodes[nodeId],
  (node) => Boolean(node?.playing),
)
export const getNodeTransformState = createSelector(
  ({ state }: IProjectContext) => state.nodes,
  (_state: IProjectContext, nodeId: string | null) => nodeId,
  (nodes, nodeId) => (nodeId ? nodes[nodeId]?.transform : null),
)
export const getIsTransforming = createSelector(
  (state: IProjectContext) => state.state.nodes,
  (_state: IProjectContext, nodeIds: string[]) => nodeIds,
  (nodes, nodeIds) => R.any((state) => Boolean(state.transform), R.values(R.pick(nodeIds, nodes))),
)

export const getNodeAnimationState = createSelector(
  (state: IProjectContext) => state.state.nodes,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (nodes, nodeId) => nodes[nodeId]?.playing,
)
export const getIsNodeOpen = createSelector(
  (state: IProjectContext) => getBlock(state),
  (state: IProjectContext) => state.state.editor.selectedNodes,
  (state: IProjectContext) => state.state.nodes,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (block, selectedNodes, nodesState, nodeId) => {
    const isRoot = block?.schema.rootId === nodeId
    const parents = (isRoot ? [] : selectedNodes)
      .map((id) => {
        const path = (block && id && getPathUnsafe(block, id)?.slice(0, -1)) || []
        return path.map((id) => block && (block.schema.nodes[id] as NodeV2)).filter(isNodeV2)
      })
      .flat()
      .map(({ id }) => id)

    const nodes = (isRoot ? Object.values(block.schema.nodes) : [block?.schema.nodes[nodeId]])
      .filter(isNodeV2)
      .map((node) => nodesState[node.id])

    return parents.includes(nodeId) || nodes.some((state) => state?.open)
  },
)

export const getIsMaxZIndex = createSelector(
  (state: IProjectContext) => state.data.blocks,
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, blockId: string) => blockId,
  (_state: IProjectContext, _blockId: string, nodes: NodeV2 | NodeV2[]) => nodes,
  (blocks, device, blockId, nodes) => {
    const nodeIds = (Array.isArray(nodes) ? nodes : [nodes]).map((n) => n.id)
    const block = blocks[blockId]
    const children = getParentNode(nodeIds[0], block)?.children || []
    const styles = children
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)
      .map(({ id, style }) => ({
        id,
        zIndex: Math.abs(mergeNodeStyle(style, device).zIndex || 0),
      }))
    const max = Math.max(
      ...styles.filter((i) => !nodeIds.includes(i.id)).map(({ zIndex }) => zIndex),
    )
    const zIndex = Math.min(...styles.filter((i) => nodeIds.includes(i.id)).map((i) => i.zIndex))

    return children.length < 2 || zIndex > max
  },
)

export const getIsMinZIndex = createSelector(
  (state: IProjectContext) => state.data.blocks,
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, blockId: string) => blockId,
  (_state: IProjectContext, _blockId: string, nodes: NodeV2 | NodeV2[]) => nodes,
  (blocks, device, blockId, nodes) => {
    const nodeIds = (Array.isArray(nodes) ? nodes : [nodes]).map((n) => n.id)
    const block = blocks[blockId]
    const children = getParentNode(nodeIds[0], block)?.children || []
    const styles = children
      .map((id) => block.schema.nodes[id])
      .filter(isNodeV2)
      .map(({ id, style }) => ({
        id,
        zIndex: Math.abs(mergeNodeStyle(style, device).zIndex || 0),
      }))
    const min = Math.min(
      ...styles.filter((i) => !nodeIds.includes(i.id)).map(({ zIndex }) => zIndex),
    )
    const zIndex = Math.max(...styles.filter((i) => nodeIds.includes(i.id)).map((i) => i.zIndex))

    return children.length < 2 || zIndex < min
  },
)

export const getIsInFlow = createSelector(
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (device, nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      const style = mergeNodeStyle(node.style, device)
      return style.position !== 'absolute'
    }),
)

export const getIsGrow = createSelector(
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (device, nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      const style = mergeNodeStyle(node.style, device)
      return style.flexGrow
    }),
)

export const getIsFlexFlow = createSelector(
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (device, nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      const style = mergeNodeStyle(node.style, device)
      return style.flexFlow === 'absolute'
    }),
)

export const getIsShrink = createSelector(
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (device, nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      const style = mergeNodeStyle(node.style, device)
      return style.flexShrink
    }),
)

export const getIsHidden = createSelector(
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (device, nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      const style = mergeNodeStyle(node.style, device)
      return style.hidden
    }),
)

export const getIsLocked = createSelector(
  (_state: IProjectContext, nodes: NodeV2 | NodeV2[]) => nodes,
  (nodes) =>
    (Array.isArray(nodes) ? nodes : [nodes]).every((node) => {
      return node.isDone
    }),
)

export const getUsedElements = createSelector(
  (state: IProjectContext) => state.data.blocks,
  (state: IProjectContext) => state.state.deviceMode,
  (_state: IProjectContext, blockId?: string) => blockId,
  (blocks, device, blockId) => {
    const block = blockId && blocks[blockId]
    if (!block) {
      return EMPTY_ARRAY
    }
    const { rootId } = block.schema || {}
    const usedNodes = getNodeWithFullChildren(block, rootId, device)
    const shells = usedNodes.filter(isShellNode)
    const usedElementIds: string[] = shells.flatMap((shell) => shell?.elementId).filter(notEmpty)
    const usedElements = Object.entries(lodash.pick(block.elements, usedElementIds)).map(
      ([id, data]) => ({ ...data, id }),
    )
    return usedElements
  },
)

export const getNotUsedNodes = createSelector(
  (state: IProjectContext, blockId?: string) => getBlock(state, { id: blockId }),
  (block) => {
    if (!block) {
      return EMPTY_ARRAY
    }
    const { rootId } = block.schema
    const usedNodes = getNodeWithFullChildren(block, rootId)
    const notUsedNodes = R.omit(usedNodes.map(R.prop('id')), block.schema.nodes) as MapType<NodeV2>
    const notUsedComplexElementNodes = R.values(notUsedNodes).flatMap((node) => {
      const element = isNodeV2(node) && node.elementId && block.elements[node.elementId]
      if (!element) {
        return []
      }
      return isComplexElement(element)
        ? element.value.items.map((item) => item.value)
        : isSlotElement(element) && element.value?.item?.value
          ? [element.value.item.value]
          : []
    })

    const notUsedChildren = R.values(notUsedNodes).flatMap((node) => node.children)
    const notUsedMainNodes = R.omit(
      [...notUsedChildren, ...notUsedComplexElementNodes],
      notUsedNodes,
    )

    return R.values(notUsedMainNodes) as NodeV2[]
  },
)

export const getParents = createSelector(
  (state: IProjectContext, blockId?: string | null) => blockId && state.data.blocks[blockId],
  (_state: IProjectContext, _blockId?: string | null, nodeId?: string | null) => nodeId,
  (block, nodeId) => {
    const path = (block && nodeId && getPathUnsafe(block, nodeId)?.slice(0, -1)) || []
    return path.map((id) => block && (block.schema.nodes[id] as NodeV2)).filter(isNodeV2)
  },
)

export const getDragFull = (state: IProjectContext) => state.state.editor.dnd

export const getDragSource = createSelector(
  (state: IProjectContext) => state.state.editor.dnd?.sourceId,
  (state: IProjectContext, id?: string) => getBlock(state, { id }),
  (sourceId, block) => {
    if (!sourceId || !block) {
      return null
    }
    const node = block.schema.nodes[sourceId]
    return isNodeV2(node) ? node : null
  },
)
export const getDragDestination = createSelector(
  (state: IProjectContext) => state.state.editor.dnd?.destinationId,
  (state: IProjectContext, id?: string) => getBlock(state, { id }),
  (destinationId, block) => {
    if (!destinationId || !block) {
      return null
    }
    const node = block.schema.nodes[destinationId]
    return isNodeV2(node) ? node : null
  },
)

export const getPlaceholders = createSelector(
  (state: IProjectContext) => state.state.editor.dnd,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (dnd, nodeId) => {
    if (dnd?.destinationId === nodeId) {
      return dnd
    }
    return null
  },
)

export const getShowCanvasPlaceholder = createSelector(
  (state: IProjectContext) => state.state.editor.dnd,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (dnd, nodeId) => {
    if (dnd?.destinationId === nodeId && dnd?.zone === 'canvas') {
      return true
    }
    return false
  },
)

export const getIsDestination = createSelector(
  (state: IProjectContext) => state.state.editor.dnd?.destinationId,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (destinationId, nodeId) => destinationId === nodeId,
)

export const getIsTarget = createSelector(
  (state: IProjectContext) => state.state.editor.dnd?.targetIds,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (targets, nodeId) => targets?.find((id) => id === nodeId),
)

export const getIsResize = createSelector(
  (state: IProjectContext) => state.state.editor.dnd?.type,
  (type) => Boolean(type && type !== 'DRAG'),
)

export const getIsDragging = ({ state: { editor } }: IProjectContext) => Boolean(editor.dnd)

const getNodeWithFullChildren = (block: Block, nodeId: string, device?: DeviceMode): NodeV2[] => {
  const node = block.schema.nodes[nodeId]

  if (!isNodeV2(node) || (device && node.style[device]?.hidden)) {
    return []
  }

  const children = getFullChildrenIds(block, nodeId)
  const childrenNodes = children.flatMap((id) => getNodeWithFullChildren(block, id, device)) || []
  return [node, ...childrenNodes]
}

const getNode2 = (block: Pick<Block, 'schema'>, nodeId: string) => block?.schema?.nodes?.[nodeId]
const getElement = (block: Pick<Block, 'elements'>, elementId: string) => block.elements[elementId]

const getFullChildrenIds = (block: Block, nodeId: string): string[] => {
  const node = getNode2(block, nodeId)
  if (isShellNode(node)) {
    if (node?.elementId) {
      const element = getElement(block, node.elementId)
      if (isComplexElement(element)) {
        return element.value.items.map((item) => item?.value).filter(notEmpty)
      }
      if (isSlotElement(element) && element.value?.item?.value) {
        return [element.value.item.value]
      }
    }
  }

  return node?.children || []
}

const getPathUnsafe = (
  block: Block,
  findId: string,
  currentId = block.schema.rootId,
): string[] | void => {
  if (currentId === findId) {
    return [findId]
  }

  const children = getFullChildrenIds(block, currentId)
  for (const id of children) {
    const path = getPathUnsafe(block, findId, id)
    if (path) {
      return [currentId, ...path]
    }
  }
}

export const getPath2 = (block: Block, nodeId: string): string[] =>
  getPathUnsafe(block, nodeId) || []

export const getPathNodes = (block: Block, nodeId: string) =>
  getPath2(block, nodeId).map((id) => getNode2(block, id))

export const getIsEditingGrid = createSelector(
  (state: IProjectContext) => state.state.nodes,
  (_state: IProjectContext, nodeId: string) => nodeId,
  (nodes, nodeId) => Boolean(nodes[nodeId]?.isEditingGrid),
)

export const getIsShowGrid = createSelector(
  (state: IProjectContext) => state.state.editor.settings.showGrid,
  (state: IProjectContext) => state.state.nodes,
  (state: IProjectContext) => state.data.blocks,
  (_state: IProjectContext, blockId: string) => blockId,
  (_state: IProjectContext, _blockId: string, nodeId: string) => nodeId,
  (showRoot, nodes, blocks, blockId, nodeId) => {
    const block = blocks[blockId]
    if (block.schema.rootId === nodeId) {
      return showRoot || Boolean(nodes[nodeId]?.showGrid)
    }
    return Boolean(nodes[nodeId]?.showGrid)
  },
)
